import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { apiUrl } from '../config/aws-config';

const ComponentStatus = () => {
  const [components, setComponents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const buildAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session?.tokens?.idToken?.toString();
      return idToken ? { Authorization: `Bearer ${idToken}` } : {};
    } catch (e) {
      console.error('Failed to build auth headers:', e);
      return {};
    }
  };

  const fetchComponentStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const authHeaders = await buildAuthHeaders();
      const response = await fetch(apiUrl('admin/component-status'), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Admin access required. You do not have permission to view component status.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setComponents(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch component status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComponentStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchComponentStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    if (status === 'Active' || status === 'ACTIVE' || status === 'configured') {
      return 'text-green-600 bg-green-50 border-green-200';
    } else if (status === 'Pending' || status === 'Pending' || status === 'InProgress') {
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    } else if (status === 'NOT_FOUND' || status === 'ERROR' || status === 'not_configured') {
      return 'text-red-600 bg-red-50 border-red-200';
    }
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getStatusBadge = (status) => {
    if (status === 'Active' || status === 'ACTIVE') {
      return '✅ Healthy';
    } else if (status === 'Pending' || status === 'InProgress') {
      return '⚠️ Pending';
    } else if (status === 'NOT_FOUND') {
      return '❌ Not Found';
    } else if (status === 'ERROR') {
      return '❌ Error';
    }
    return status;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatSchedule = (scheduleExpression) => {
    if (!scheduleExpression) return 'N/A';
    // Parse cron expression: cron(minute hour day month day-of-week year)
    // Example: cron(0 10 * * ? *) = Every day at 10:00 AM UTC
    // Example: cron(30 12 * * ? *) = Every day at 12:30 PM UTC
    const match = scheduleExpression.match(/cron\(([^)]+)\)/);
    if (match) {
      const parts = match[1].split(' ');
      if (parts.length >= 2) {
        const minute = parts[0];
        const hour = parts[1];
        return `Daily at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} UTC`;
      }
    }
    return scheduleExpression;
  };

  if (loading && !components) {
    return (
      <div className="p-6 bg-gray-50 flex items-center justify-center min-h-[12rem]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading component status...</p>
        </div>
      </div>
    );
  }

  if (error && !components) {
    const isAccessDenied = error.includes('Admin access required') || error.includes('403');
    return (
      <div className="p-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className={`border rounded-lg p-6 ${isAccessDenied ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isAccessDenied ? 'text-yellow-900' : 'text-red-900'}`}>
              {isAccessDenied ? 'Access Denied' : 'Error Loading Status'}
            </h3>
            <p className={isAccessDenied ? 'text-yellow-700' : 'text-red-700'}>{error}</p>
            {!isAccessDenied && (
              <button
                onClick={fetchComponentStatus}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const apiCheckedAt =
    components?.statusCheckedAt != null
      ? new Date(components.statusCheckedAt).toLocaleString()
      : null;

  return (
    <div className="p-4 md:p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-xl p-6 mb-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">System health</h1>
              <p className="text-gray-600 text-sm">
                Component checks from AWS (Lambda, ECS, queues, Redis, schedules).
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {apiCheckedAt && (
                  <span>
                    <span className="font-medium text-gray-700">Last API sync: </span>
                    {apiCheckedAt}
                  </span>
                )}
                {lastRefresh && (
                  <span>
                    <span className="font-medium text-gray-700">UI refresh: </span>
                    {lastRefresh.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={fetchComponentStatus}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed shrink-0"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Data integrity (RDS) — auto-approve anchor */}
        {components?.dataIntegrity != null && (
          <div
            className={`mb-6 border rounded-xl p-5 shadow-sm ${
              components.dataIntegrity.error
                ? 'bg-red-50 border-red-200'
                : components.dataIntegrity.autoApproveAnchor?.severity === 'critical'
                ? 'bg-red-50 border-red-200'
                : components.dataIntegrity.autoApproveAnchor?.severity === 'warning'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-white border-gray-200'
            }`}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Data integrity</h2>
            <p className="text-xs text-gray-600 mb-3">
              Auto-approve quarterly anchor (read-only check; no automatic repair or retries).
            </p>
            {components.dataIntegrity.error ? (
              <p className="text-sm text-red-800">
                <span className="font-medium">RDS check failed:</span> {components.dataIntegrity.error}
              </p>
            ) : (
              <>
                {(() => {
                  const a = components.dataIntegrity.autoApproveAnchor;
                  if (!a) return null;
                  const sev = a.severity || 'ok';
                  const sevClass =
                    sev === 'critical'
                      ? 'bg-red-100 text-red-800 border-red-300'
                      : sev === 'warning'
                      ? 'bg-amber-100 text-amber-900 border-amber-300'
                      : 'bg-green-100 text-green-800 border-green-300';
                  const sevLabel =
                    sev === 'critical'
                      ? 'Critical'
                      : sev === 'warning'
                      ? 'Warning'
                      : 'OK';
                  return (
                    <>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${sevClass}`}
                        >
                          {sevLabel}
                        </span>
                        <span className="text-sm text-gray-700">
                          Active mappings, auto-approve on, anchor NULL:{' '}
                          <strong>{a.activeOnWithoutAnchorCount ?? 0}</strong>
                          {' · '}
                          Inactive (same): <strong>{a.inactiveOnWithoutAnchorCount ?? 0}</strong>
                          {' · '}
                          Total: <strong>{a.totalOnWithoutAnchorCount ?? 0}</strong>
                        </span>
                      </div>
                      {a.runbookHint && (
                        <p className="text-xs text-gray-700 mb-2 leading-relaxed">{a.runbookHint}</p>
                      )}
                      {a.observabilityHint && (
                        <p className="text-xs text-gray-500 mb-3 leading-relaxed">{a.observabilityHint}</p>
                      )}
                      {Array.isArray(a.samples) && a.samples.length > 0 && (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium">Mapping ID</th>
                                <th className="text-left px-3 py-2 font-medium">User ID</th>
                                <th className="text-left px-3 py-2 font-medium">Active</th>
                                <th className="text-left px-3 py-2 font-medium">Updated</th>
                              </tr>
                            </thead>
                            <tbody>
                              {a.samples.map((row, idx) => (
                                <tr key={idx} className="border-t border-gray-100">
                                  <td className="px-3 py-2 font-mono break-all">{row.mappingId}</td>
                                  <td className="px-3 py-2 font-mono break-all">{row.userId}</td>
                                  <td className="px-3 py-2">{row.isActive ? 'Yes' : 'No'}</td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {row.updatedAt ? String(row.updatedAt) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* Lambda Functions — card grid */}
        {components?.lambdas && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Lambda functions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {components.lambdas.map((lambda, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                      {lambda.name.replace('tradingbot-', '')}
                    </h3>
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border shrink-0 ${getStatusColor(
                        lambda.status
                      )}`}
                    >
                      {getStatusBadge(lambda.status)}
                    </span>
                  </div>
                  <dl className="text-xs text-gray-600 space-y-1.5">
                    <div>
                      <dt className="text-gray-500 inline">Runtime: </dt>
                      <dd className="inline">{lambda.runtime || 'N/A'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">Package size: </dt>
                      <dd className="inline">{formatBytes(lambda.codeSize)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">Last code update: </dt>
                      <dd className="inline">
                        {lambda.lastModified
                          ? new Date(lambda.lastModified).toLocaleString()
                          : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                  {lambda.schedules && lambda.schedules.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-700 mb-1">Schedules</p>
                      <div className="space-y-1">
                        {lambda.schedules.map((schedule, idx) => (
                          <div key={idx} className="text-xs">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded ${
                                schedule.state === 'ENABLED'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {formatSchedule(schedule.scheduleExpression)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {lambda.error && <p className="text-xs text-red-600 mt-2">{lambda.error}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ECS Services */}
        {components?.ecs_services && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">ECS services</h2>
            {apiCheckedAt && (
              <p className="text-xs text-gray-500 mb-3">Status as of last API sync above.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {components.ecs_services.map((service, index) => (
                <div
                  key={index}
                  className={`border border-gray-200 rounded-xl p-4 shadow-sm bg-white ${getStatusColor(service.status)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">
                      {service.name.replace('tradingbot-', '')}
                    </h3>
                    <span className="text-xs font-semibold">
                      {getStatusBadge(service.status)}
                    </span>
                  </div>
                  {service.cluster && (
                    <p className="text-sm text-gray-600 mb-2">Cluster: {service.cluster}</p>
                  )}
                  {service.running !== undefined && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        Tasks: {service.running}/{service.desired} running
                        {service.pending > 0 && `, ${service.pending} pending`}
                      </p>
                    </div>
                  )}
                  {service.schedules && service.schedules.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Schedules:</p>
                      <div className="space-y-1">
                        {service.schedules.map((schedule, idx) => (
                          <div key={idx} className="text-xs">
                            <span className={`inline-flex px-2 py-0.5 rounded ${
                              schedule.state === 'ENABLED' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {formatSchedule(schedule.scheduleExpression)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {service.error && (
                    <p className="text-xs text-red-600 mt-2">{service.error}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All EventBridge Schedules Overview */}
        {components?.schedules && components.schedules.length > 0 && (
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">EventBridge Schedules</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rule Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schedule
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      State
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Targets
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {components.schedules.map((schedule, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {schedule.ruleName.replace('tradingbot-', '')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatSchedule(schedule.scheduleExpression)}
                        </span>
                        <br />
                        <span className="text-xs text-gray-500">
                          {schedule.scheduleExpression}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            schedule.state === 'ENABLED'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {schedule.state}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {schedule.description || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {schedule.targets && schedule.targets.length > 0 ? (
                          <div className="space-y-1">
                            {schedule.targets.map((target, idx) => (
                              <span key={idx} className="inline-block bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs mr-1">
                                {target}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">No targets</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SQS Queues */}
        {components?.queues && components.queues.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">SQS queues</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {components.queues.map((queue, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-gray-900 text-sm break-all">
                      {queue.name.replace('tradingbot-', '').replace('.fifo', '')}
                    </h3>
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border shrink-0 ${
                        queue.status === 'active'
                          ? 'text-green-600 bg-green-50 border-green-200'
                          : queue.status === 'not_found'
                          ? 'text-red-600 bg-red-50 border-red-200'
                          : 'text-yellow-600 bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      {queue.status === 'active'
                        ? 'Active'
                        : queue.status === 'not_found'
                        ? 'Not found'
                        : 'Error'}
                    </span>
                  </div>
                  <dl className="text-xs text-gray-600 space-y-1">
                    <div>
                      <dt className="text-gray-500 inline">Available: </dt>
                      <dd className="inline">
                        {queue.messages_available !== undefined ? queue.messages_available : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">In flight: </dt>
                      <dd className="inline">
                        {queue.messages_in_flight !== undefined ? queue.messages_in_flight : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">Delayed: </dt>
                      <dd className="inline">
                        {queue.messages_delayed !== undefined ? queue.messages_delayed : 'N/A'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500 inline">Visibility: </dt>
                      <dd className="inline">
                        {queue.visibility_timeout !== undefined
                          ? `${queue.visibility_timeout}s`
                          : 'N/A'}
                      </dd>
                    </div>
                  </dl>
                  {queue.error && <p className="text-xs text-red-600 mt-2">{queue.error}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Redis */}
        {components?.redis && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Redis cache</h2>
            <div
              className={`bg-white border border-gray-200 rounded-xl p-4 shadow-sm max-w-xl ${getStatusColor(
                components.redis.status
              )}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h3 className="font-medium text-gray-900">Connection</h3>
                  {components.redis.host && (
                    <p className="text-sm text-gray-600 mt-1">Host: {components.redis.host}</p>
                  )}
                  {apiCheckedAt && (
                    <p className="text-xs text-gray-500 mt-2">Last check: {apiCheckedAt}</p>
                  )}
                </div>
                <span className="text-xs font-semibold shrink-0">
                  {components.redis.status === 'configured'
                    ? 'Configured'
                    : components.redis.status === 'not_configured'
                    ? 'Not configured'
                    : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentStatus;
